import React from 'react'
import copy from 'copy-to-clipboard'
import styled from 'styled-components'
import { useTrackMutation } from '@mote/api'
import {
  BackIcon,
  CloseIcon,
  EmbedIcon,
  LinkIcon,
  QrCodeIcon,
  TickIcon
} from '@mote/assets/icons'
import { MOTE_LINK_DOMAIN, TRACK_EVENT_NAMES } from '@mote/helpers/constants'
import { Block, Text } from '@mote/ui'
import GoogleClassroomIcon from './GoogleClassroomIcon.png'
import { EmbedCodeBox } from './embedCodeBox'
import MoteLogo from './mote-logo.png'
import { QrCodeDisplay } from './qrCodeDisplay'
import { ShareOptions } from './shareOptions'

export const SharePanelExtension = ({ onClose, moteId, canRedirect }) => {
  const [showNotification, setShowNotification] = React.useState(false)
  const [showQrCode, setShowQrCode] = React.useState(false)
  const [showEmbedCode, setShowEmbedCode] = React.useState(false)
  const { mutate: track } = useTrackMutation()

  const showBackButton = React.useMemo(
    () => Boolean(showQrCode || showEmbedCode),
    [showQrCode, showEmbedCode]
  )
  const shareToText = React.useMemo(() => {
    if (showQrCode) return 'QR Code'
    if (showEmbedCode) return 'Embed'

    return 'Share to...'
  }, [showQrCode, showEmbedCode])

  const sharingUrl = `${MOTE_LINK_DOMAIN}${moteId}`

  const options = [
    {
      icon: <LinkIcon />,
      style: {
        backgroundColor: 'rgba(213, 212, 218, 0.2)',
        border: '1px solid rgba(213, 212, 218, 0.5)'
      },
      text: 'Copy link',
      onClick: handleCopyLink,
      canRender: true
    },
    {
      icon: <EmbedIcon />,
      style: {
        backgroundColor: '#232323',
        border: '1px solid #232323'
      },
      text: 'Embed',
      onClick: handleEmbedClick,
      canRender: true
    },
    {
      icon: <QrCodeIcon />,
      style: {
        backgroundColor: '#F16587',
        border: '1px solid #F16587'
      },
      text: 'QR code',
      onClick: handleQrCodeGenerator,
      canRender: true
    },
    {
      icon: <img src={GoogleClassroomIcon} />,
      text: 'Google Classroom',
      onClick: handleGoogleClassroomShare,
      canRender: canRedirect
    }
  ]

  function handleCopyLink() {
    if (showQrCode) setShowQrCode(false)
    copy(sharingUrl)
    setShowNotification(true)
    track({
      variables: {
        event: TRACK_EVENT_NAMES.sharePanel.action,
        action: 'Copy-to-clipboard'
      }
    })
  }

  async function handleQrCodeGenerator() {
    if (showNotification) setShowNotification(false)
    setShowQrCode(true)

    track({
      variables: {
        event: TRACK_EVENT_NAMES.sharePanel.action,
        action: 'QR-code-viewed'
      }
    })
  }

  async function handleGoogleClassroomShare() {
    track({
      variables: {
        event: TRACK_EVENT_NAMES.sharePanel.action,
        action: 'Share-to-google-classroom'
      }
    })
    window.open(
      `https://classroom.google.com/u/0/share?url=${sharingUrl}`,
      '_blank',
      'location=yes,height=400,width=650,scrollbars=yes,status=yes'
    )
  }

  function handleBackButtonClick() {
    if (showQrCode) {
      setShowQrCode(false)
    }

    if (showEmbedCode) {
      setShowEmbedCode(false)
    }
  }

  function handleEmbedClick() {
    if (showNotification) setShowNotification(false)
    setShowEmbedCode(true)

    track({
      variables: {
        event: TRACK_EVENT_NAMES.sharePanel.action,
        action: 'Embed-code-viewed'
      }
    })
  }

  function handleCopyEmbedCode() {
    setShowEmbedCode(false)

    setShowNotification('Code copied to clipboard')
  }

  return (
    <BottomPane>
      <TitleContainer layout='horizontal' justify='center'>
        {showBackButton && <BackIconStyled onClick={handleBackButtonClick} />}
        <Text tag='p' fontSize='18'>
          {shareToText}
        </Text>
        <CloseIconStyled color='#232323' onClick={() => onClose?.()} />
      </TitleContainer>
      <Block margin='16px 16px 16px 16px'>
        {!showQrCode && !showEmbedCode && <ShareOptions options={options} />}
        {showQrCode && (
          <QrCodeDisplay
            logoImage={MoteLogo}
            url={sharingUrl}
            moteId={moteId}
          />
        )}
        {showEmbedCode && (
          <EmbedCodeBox
            url={sharingUrl}
            onCopyEmbedCode={handleCopyEmbedCode}
            app='extension'
          />
        )}
      </Block>
      {showNotification && (
        <Notification
          align='center'
          justify='center'
          gap={10}
          layout='horizontal'
        >
          <TickIcon color='white' />
          <Text tag='p' fontSize={16} color='white'>
            Link copied to clipboard
          </Text>
        </Notification>
      )}
    </BottomPane>
  )
}

const BottomPane = styled(Block)`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  width: 100%;
  height: 85vh;
  max-width: 360px;
  background: white;
  border-top-left-radius: 6px;
  border-top-right-radius: 6px;
  border: 1px solid rgba(213, 212, 218, 0.5);
`

const CloseIconStyled = styled(CloseIcon)`
  cursor: pointer;
  position: absolute;
  top: 13px;
  right: 16px;
`

const TitleContainer = styled(Block)`
  position: relative;
  padding: 17px 0;
  border-bottom: 1px solid rgba(213, 212, 218, 0.5);
`

const Notification = styled(Block)`
  position: absolute;
  bottom: 27px;
  background: #232323;
  box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.1);
  border-radius: 40px;
  height: 50px;
  left: 25px;
  right: 25px;
`

const BackIconStyled = styled(BackIcon)`
  cursor: pointer;
  position: absolute;
  top: 23px;
  left: 16px;
`
